import { useEffect, useState } from "react";
import { Container, Divider, Grid, Stack, Typography } from "@mui/material";

import dayjs from "dayjs";
import "dayjs/locale/es-mx";
import { UUID } from "crypto";
import { RootState, store } from "@store/store";
import { formatCurrency } from "@helpers/formats";

import { filterByNestedCodes } from "@helpers/generals";
import { useLazyGetPersonsQuery } from "@services/api/persons";
import { useLazyGetAccountQuery } from "@services/api/accounts";
import { useLazyGetAddressesQuery } from "@services/api/address";
import { useLazyGetPortalUsersQuery } from "@services/api/portal_users";
import {
    useLazyGetApplicationQuery,
    useLazyGetConditionsQuery,
    useLazyGetDestinationResourceQuery,
} from "@services/api/applications";
import { ApplicationResume } from "@interfaces/application";
import { useSelector } from "react-redux";
import { useGetPermission } from "@hooks/useGetPermission";

interface LoanApplicationInfoProps {
    // loanAppId: number;
    // fullData: string;
    data: ApplicationResume;
}

interface ApplicationData {
    applicationId: number;
    startDate: string;
    approvedAmount: number;
    approvedTerm: number;
    requestedTerm: number;
    loanAmount: number;
    creditUse: string;
}

interface UserData {
    userId: UUID;
    fullName: string;
    email: string;
    phone: string;
}

interface CompanyData {
    business_description: string;
    state: string;
    guarantee_name: string;
}

export const LoanApplicationInfo = (props: LoanApplicationInfoProps) => {
    const [applicationData, setApplicationData] = useState<ApplicationData>();
    const [userData, setUserData] = useState<UserData>();
    const [companyData, setCompanyData] = useState<CompanyData>({
        business_description: "-",
        guarantee_name: "-",
        state: "-",
    });

    const permission = useGetPermission("SBIUMS");

    const sessionInfo = useSelector((state: RootState) => state.session.user);

    const [triggerGetPerson] = useLazyGetPersonsQuery();
    const [triggerGetAccount] = useLazyGetAccountQuery();
    const [triggerGetAddress] = useLazyGetAddressesQuery();
    const [triggerGetConditions] = useLazyGetConditionsQuery();
    const [triggerGetPortalUsers] = useLazyGetPortalUsersQuery();
    const [triggerGetApplication] = useLazyGetApplicationQuery();

    const [triggerGetDestinationResource] = useLazyGetDestinationResourceQuery();

    const loadInfo = async () => {
        let searchParams = new URLSearchParams([["q", "active:true"]]);
        const conditions = await triggerGetConditions({
            applicationId: props.data.id,
            params: searchParams.toString(),
        }).unwrap();

        const loanAppConditionActive = conditions.find((condition) => condition.active === true);
        const result = await triggerGetApplication(props.data.id, true).unwrap();

        const destinations = await triggerGetDestinationResource(props.data.id).unwrap();

        if (loanAppConditionActive) {
            setApplicationData({
                applicationId: props.data.id_application_company,
                startDate: dayjs(result?.created_at ?? null).isValid()
                    ? dayjs(result?.created_at).utc(true).local().locale("es-mx").format("DD/MM/YYYY")
                    : "--/--/----",
                approvedAmount: loanAppConditionActive ? loanAppConditionActive.approved_amount : 0,
                approvedTerm: loanAppConditionActive ? loanAppConditionActive.approved_term : 0,
                requestedTerm: loanAppConditionActive ? loanAppConditionActive.requested_term : 0,
                loanAmount: loanAppConditionActive ? loanAppConditionActive.loan_amount : 0,
                creditUse: destinations?.reason ?? "-",
            });
        }

        const accountData = await triggerGetAccount(props.data.client_detail_id).unwrap();

        searchParams = new URLSearchParams([["q", `client_details_id:${props.data.client_detail_id}`]]);
        const address = await triggerGetAddress(searchParams.toString()).unwrap();
        const operativeAddress = address.find((t) => t.address_type.code === "CDOP");

        searchParams = new URLSearchParams([["q", `loan_application_id:${props.data.id}`]]);
        const persons = await triggerGetPerson(searchParams.toString()).unwrap();

        const guarantee = filterByNestedCodes(persons, (person) => person.person_types_detail, ["PTAM"]).at(0);

        setCompanyData({
            business_description: accountData.business_description,
            state: operativeAddress ? operativeAddress.state.name : "-",
            guarantee_name: guarantee ? [guarantee.name, guarantee.last_name, guarantee.last_name_2].join(" ") : "-",
        });

        const portalUserData = await triggerGetPortalUsers(accountData.user_id).unwrap();

        setUserData({
            userId: accountData.user_id,
            fullName: [accountData.name, accountData.last_name, accountData.last_name_2].join(" "),
            email: portalUserData.email,
            phone: portalUserData.phone,
        });
    };

    useEffect(() => {
        if (props.data.id === "0-0-0-0-0" || props.data.id === undefined) return;

        loadInfo();
    }, [props.data.id]);

    const createRowInfo = (description: string, value: string) => {
        return (
            <Typography
                variant="body1"
                component={"span"}
                sx={{
                    my: 1,
                }}
            >
                {description}:{" "}
                <Typography
                    variant="body1"
                    component={"span"}
                    sx={{
                        fontWeight: 600,
                    }}
                >
                    {value}
                </Typography>
            </Typography>
        );
    };

    return (
        <Container maxWidth="xs" sx={{ px: "0 !important", marginRight: 2 }}>
            <Grid
                container
                sx={{
                    display: "flex",
                    flexDirection: "row",
                }}
            >
                <Grid
                    size={1}
                    sx={{
                        alignContent: "center",
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            fontWeight: 500,
                            color: "#528CD6",
                            transform: "rotate(-90deg)",
                            marginBottom: "-20px",
                        }}
                    >
                        SOLICITUD
                    </Typography>
                </Grid>
                <Grid size={11}>
                    <Stack>
                        {createRowInfo("Solicitud", applicationData?.applicationId.toString() ?? "")}
                        {createRowInfo("Fecha de inicio", applicationData?.startDate ?? "")}
                        {createRowInfo(
                            "Monto max. autorizado",
                            formatCurrency(applicationData?.approvedAmount ?? 0, "$", 0),
                        )}
                        {createRowInfo("Monto seleccionado", formatCurrency(applicationData?.loanAmount ?? 0, "$", 0))}
                        {createRowInfo("Plazo max. autorizado", applicationData?.approvedTerm.toString() ?? "")}
                        {createRowInfo("Plazo seleccionado", applicationData?.requestedTerm.toString() ?? "")}
                        {createRowInfo("Uso del crédito", applicationData?.creditUse ?? "")}
                    </Stack>
                </Grid>
                <Grid size={1}></Grid>
                <Grid size={10}>
                    <Divider sx={{ borderColor: "#528CD6", borderWidth: "1" }} />
                </Grid>
                <Grid size={1}></Grid>
                <Grid
                    size={1}
                    sx={{
                        alignContent: "center",
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            fontWeight: 500,
                            color: "#528CD6",
                            transform: "rotate(-90deg)",
                            marginBottom: "-20px",
                        }}
                    >
                        EMPRESA
                    </Typography>
                </Grid>

                <Grid size={11}>
                    <Stack>
                        {createRowInfo("Giro", companyData?.business_description)}
                        {createRowInfo("Entidad federativa", companyData?.state)}
                        {createRowInfo("Aval", companyData?.guarantee_name)}
                    </Stack>
                </Grid>
                <Grid size={1}></Grid>
                <Grid size={10}>
                    <Divider sx={{ borderColor: "#528CD6", borderWidth: "1" }} />
                </Grid>
                <Grid size={1}></Grid>
                {props.data.company_source !== "PYMIO" &&
                    (sessionInfo.role.code === "RADM2" || permission?.can_watch === true) && (
                        <>
                            <Grid
                                size={1}
                                sx={{
                                    alignContent: "center",
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 500,
                                        color: "#528CD6",
                                        transform: "rotate(-90deg)",
                                        marginBottom: "-20px",
                                    }}
                                >
                                    USUARIO
                                </Typography>
                            </Grid>
                            <Grid size={11}>
                                <Stack>
                                    {createRowInfo("ID Usuario", userData?.userId.toString() ?? "")}
                                    {createRowInfo("Nombre Completo", userData?.fullName ?? "")}
                                    {createRowInfo("Correo", userData?.email ?? "")}
                                    {createRowInfo("Teléfono", userData?.phone ?? "")}
                                </Stack>
                            </Grid>
                        </>
                    )}
            </Grid>
        </Container>
    );
};
